import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '@/utils/BaseQuery';
import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import {
  IYachtData,
  YatchListQueryParams,
} from '@/types/yacht-management.types';
import { IClubData } from '@/types/club-management.types';
import { HarborKYCData } from '@/types/harbor-management.types';
import { KYCType } from '@/constant';

export interface YachtsApiResponse extends PaginatedApiResponse<IYachtData[]> {
  data: IYachtData[];
  totalPages: number;
  currentPage: number;
  pageLimit: number;
}

export const yachtManagementApi = createApi({
  reducerPath: 'yachtManagementApi',
  baseQuery,
  tagTypes: ['Yachts'],
  endpoints: (builder) => ({
    getYachts: builder.query<YachtsApiResponse, YatchListQueryParams>({
      query: (params) => ({
        url: 'clubs',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((yacht) => ({
                type: 'Yachts' as const,
                id: yacht.id,
              })),
              { type: 'Yachts', id: 'LIST' },
            ]
          : [{ type: 'Yachts', id: 'LIST' }],
      keepUnusedDataFor: 0,
    }),

    createYacht: builder.mutation<
      ApiResponse<IYachtData>,
      FormData | Partial<IYachtData>
    >({
      query: (data) => ({
        url: 'clubs',
        method: 'POST',
        data,
        headers:
          data instanceof FormData
            ? {}
            : { 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: [{ type: 'Yachts', id: 'LIST' }],
    }),

    updateYacht: builder.mutation<
      ApiResponse<IYachtData>,
      { yacht_id: string; data: FormData | Partial<IYachtData> }
    >({
      query: ({ yacht_id, data }) => ({
        url: `clubs/${yacht_id}`,
        method: 'PATCH',
        data,
        headers:
          data instanceof FormData
            ? {}
            : { 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: (result, error, { yacht_id }) => [
        { type: 'Yachts', id: yacht_id },
        { type: 'Yachts', id: 'LIST' },
      ],
    }),

    deleteYacht: builder.mutation<ApiResponse<void>, string>({
      query: (id) => ({
        url: `clubs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'Yachts', id },
        { type: 'Yachts', id: 'LIST' },
      ],
    }),

    getClubById: builder.query<IClubData, string>({
      query: (id) => ({
        url: `clubs/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Yachts', id }],
    }),

    getAdminCandidates: builder.query<
      PaginatedApiResponse<IClubData>,
      { search?: string }
    >({
      query: (params) => ({
        url: 'clubs/admin-candidates',
        method: 'GET',
        params: params || undefined,
      }),
      providesTags: [{ type: 'Yachts', id: 'LIST' }],
    }),
    getAdminCandidate: builder.query<IClubData, string>({
      query: (id) => ({
        url: `clubs/admin-candidates/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'Yachts', id }],
    }),

    getClubKYC: builder.query<ApiResponse<HarborKYCData>, string>({
      query: (entityId) => ({
        url: `kyc/entity/${KYCType.CLUB}/${entityId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      providesTags: (result, error, entityId) => [
        { type: 'Yachts', id: `${entityId}-kyc` },
      ],
      keepUnusedDataFor: 0,
    }),
  }),
});

export const {
  useGetYachtsQuery,
  useCreateYachtMutation,
  useUpdateYachtMutation,
  useDeleteYachtMutation,
  useGetClubByIdQuery,
  useGetAdminCandidatesQuery,
  useGetAdminCandidateQuery,
  useGetClubKYCQuery,
} = yachtManagementApi;
